import React, { memo, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { fetchMarketplaceLoad } from 'utils/api/marketplace'
import { FeatureFlagsContext } from 'utils/context/FeatureFlagsContext'
import { LoadStatusFunctionalTag } from 'apps/fleet-planner/components/atoms/LoadStatusFunctionalTag'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { UserContext } from 'apps/fleet-planner/context'
import { ILoad, ILoadStatusOption } from 'apps/fleet-planner/types'
import { updateLoadStatus } from 'apps/fleet-planner/utils/actions/loadDetails'
import { areRequiredMpStopsValid } from 'apps/fleet-planner/utils/checkinCheckoutTimesHelpers'
import { selectLoadById } from 'apps/fleet-planner/utils/selectors'
import { formatStop, getFirstStop, getLastStop } from 'apps/fleet-planner/utils/stops'
import { IStore } from 'apps/fleet-planner/utils/types/store'
import { TableRow, TableCell, ToastState } from 'transfix-ui'
import { DISPLAY } from 'constants/display'
import { LoadStatusFunctionalTagMarketplace } from '../../components/atoms/LoadStatusFunctionalTagMarketplace'
import { CustomerDetails } from './CustomerDetails'
import { LastActivity } from './LastActivity'
import { LoadActions } from './LoadActions'
import { LoadDrivers } from './LoadDrivers'
import { StopDateTime } from './StopDateTime'
import { obscurePostDeliveryStatus } from './utils'

interface ILoadsTableRowProps {
  loadId: string
  setToast: (newToastState: Omit<ToastState, 'toastOpen'>) => void
}

export const LoadsTableRow = memo<ILoadsTableRowProps>(({ loadId, setToast }) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const load: ILoad = useSelector((state: IStore) => selectLoadById(state, loadId))
  const {
    isExternal,
    externalId,
    customer,
    status: loadStatus,
    currentStopSequence,
    stops,
    trailer,
    mostRecentActivity,
    drivers = [],
  } = load || {}

  const status = obscurePostDeliveryStatus(loadStatus, isExternal)

  const firstStop = getFirstStop(load)
  const lastStop = getLastStop(load)
  const { customerLabels = [] } = load || {}
  // Time format settings set in /fleet-planner/user/settings
  const { pathname = '' } = useLocation()
  const { timeFormats, userData } = useContext(UserContext)
  const { user } = userData || {}

  const rowClickHandler = () => history.push(`/loads/${loadId}`)
  const { getFlag } = useContext(FeatureFlagsContext)
  const { enabled: isFpCarrierPortalEnabled } = getFlag('fpCarrierPortal')

  const statusChangeHandler = ({
    currentStopSequence: newCurrentStopSequence,
    status: newStatus,
  }: ILoadStatusOption) => dispatch(updateLoadStatus(load, newStatus, newCurrentStopSequence))

  // There is a marketplaceStatusChangeHandler in src/apps/fleet-planner/components/load-details/LoadStatus.tsx as well
  const marketplaceStatusChangeHandler = async ({
    currentStopSequence: newCurrentStopSequence,
    status: newStatus,
  }: ILoadStatusOption) => {
    // Prevent status updates if there isn't a driver assigned to the load
    if (!drivers.length) {
      return setToast({
        toastType: 'alert',
        toastMessage: `Please assign a driver before updating this load's status.`,
      })
    }
    if (isExternal && externalId) {
      // Fetch marketplace load for manual eta if load is external
      const marketplaceId = externalId.replace('TFX-', '')
      const { shipment: marketplaceShipment } = await fetchMarketplaceLoad(marketplaceId)

      const { status: marketplaceStatus, stops: marketplaceStops = [] } = marketplaceShipment || {}
      const { manualEta } = marketplaceStatus || {}

      // If shipment status is being transitioned to dispatched
      if (newStatus === LOAD_STATE.Dispatched) {
        if (!manualEta) {
          const query = queryString.stringify({
            loadId,
          })
          // Open pickup ETA modal if there is no manual eta
          return history.push(`${pathname}/update-eta?${query}`)
        }
        // Otherwise dispatch the shipment
        return statusChangeHandler({
          status: newStatus,
          currentStopSequence: newCurrentStopSequence,
        })
      }

      // If marketplace already has the required checkin or checkout times, skip the Checkin and Checkout modal and just update the status.
      const areMpStopTimesAlreadyPresent = areRequiredMpStopsValid(
        marketplaceStops,
        newStatus as LOAD_STATE,
        newCurrentStopSequence || ''
      )
      if (areMpStopTimesAlreadyPresent) {
        return statusChangeHandler({
          status: newStatus,
          currentStopSequence: newCurrentStopSequence,
        })
      }
    }

    // For any other state transitions open the Checkin and Checkout modal with status and stopSequence params
    const query = queryString.stringify({
      status: newStatus,
      stopSequence: newCurrentStopSequence,
      loadId,
    })
    return history.push(`${pathname}/update-status?${query}`)
  }

  return (
    <TableRow className='fp-loads-list-row' onClick={rowClickHandler}>
      <TableCell dataTest='list-row-load-id'>{externalId || DISPLAY.empty}</TableCell>
      <TableCell>
        {customer?.name || DISPLAY.empty}
        <CustomerDetails customerLabels={customerLabels} />
      </TableCell>
      <TableCell>
        {formatStop(firstStop)}
        <StopDateTime
          stop={firstStop}
          loadId={loadId}
          timeFormat={timeFormats.listStopAppointment}
        />
      </TableCell>
      <TableCell>
        {formatStop(lastStop)}
        <StopDateTime
          stop={lastStop}
          loadId={loadId}
          timeFormat={timeFormats.listStopAppointment}
        />
      </TableCell>
      <TableCell>{trailer?.trailerNumber || DISPLAY.empty}</TableCell>
      <TableCell>
        <LoadDrivers load={load} />
      </TableCell>
      <TableCell>
        {isExternal && isFpCarrierPortalEnabled ? (
          <LoadStatusFunctionalTagMarketplace
            status={status}
            currentStopSequence={Number(currentStopSequence)}
            onChange={marketplaceStatusChangeHandler}
            stops={stops}
            dataTest='status-select'
          />
        ) : (
          <LoadStatusFunctionalTag
            status={status}
            currentStopSequence={Number(currentStopSequence)}
            onChange={statusChangeHandler}
            stops={stops}
            dataTest='status-select'
          />
        )}
      </TableCell>
      <TableCell type='icon'>
        <LastActivity activity={mostRecentActivity} currentUser={user} loadStops={stops} />
      </TableCell>
      <TableCell type='icon'>
        <LoadActions load={load} />
      </TableCell>
    </TableRow>
  )
})
